import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  Calendar,
  Check,
  X,
  Clock,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Phone,
  Video,
  Filter,
} from 'lucide-react';
import type { TrainingRequest, TeamRecruitData } from '../types';
import { salesManagerService } from '../services/sales-manager.service';

// Funnel step colors (same as OnboardingFunnel)
const FUNNEL_COLORS = [
  '#3b82f6', // blue - Total Invites
  '#8b5cf6', // purple - Opened Invites
  '#06b6d4', // cyan - Registered
  '#84cc16', // lime - Docs Signed
  '#f59e0b', // amber - Training Done
  '#38bdf8', // sky - Test Passed
  '#10b981', // emerald - Ready to Sell
  '#14b8a6', // teal - First Sale
  '#22c55e', // green - First Paycheck
];

const FUNNEL_STEPS = [
  { step: 1, name: 'Total Invites' },
  { step: 2, name: 'Opened Invites' },
  { step: 3, name: 'Registered' },
  { step: 4, name: 'Docs Signed' },
  { step: 5, name: 'Training Done' },
  { step: 6, name: 'Test Passed' },
  { step: 7, name: 'Ready to Sell' },
  { step: 8, name: 'First Sale' },
  { step: 9, name: 'First Paycheck' },
];

// Funnel Visualization Component
const FunnelVisualization: React.FC<{
  data: TeamRecruitData['funnelStats'];
  selectedStep: number | null;
  onStepClick: (step: number) => void;
}> = ({ data, selectedStep, onStepClick }) => {
  const totalWidth = 600;
  const stepHeight = 50;
  const gap = 4;
  const minWidth = 120;
  const maxWidth = totalWidth;

  return (
    <div className="flex flex-col items-center py-6">
      <svg
        viewBox={`0 0 ${totalWidth} ${data.length * (stepHeight + gap)}`}
        className="w-full max-w-2xl"
        preserveAspectRatio="xMidYMid meet"
      >
        {data.map((step, index) => {
          const topWidthRatio = 1 - (index / data.length) * 0.7;
          const bottomWidthRatio = 1 - ((index + 1) / data.length) * 0.7;
          const topWidth = minWidth + (maxWidth - minWidth) * topWidthRatio;
          const bottomWidth = minWidth + (maxWidth - minWidth) * bottomWidthRatio;

          const centerX = totalWidth / 2;
          const y = index * (stepHeight + gap);

          const topLeft = centerX - topWidth / 2;
          const topRight = centerX + topWidth / 2;
          const bottomLeft = centerX - bottomWidth / 2;
          const bottomRight = centerX + bottomWidth / 2;

          const path = `M ${topLeft} ${y} L ${topRight} ${y} L ${bottomRight} ${y + stepHeight} L ${bottomLeft} ${y + stepHeight} Z`;

          const isSelected = selectedStep === step.step;

          return (
            <g
              key={step.step}
              onClick={() => onStepClick(step.step)}
              className="cursor-pointer transition-opacity hover:opacity-90"
            >
              <path
                d={path}
                fill={step.color}
                stroke={isSelected ? '#1f2937' : 'transparent'}
                strokeWidth={isSelected ? 3 : 0}
                className="transition-all duration-200"
              />
              <text
                x={centerX}
                y={y + stepHeight / 2}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="white"
                fontSize="14"
                fontWeight="600"
                className="pointer-events-none"
              >
                {step.name}
              </text>
              <text
                x={centerX}
                y={y + stepHeight / 2 + 16}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="white"
                fontSize="12"
                opacity="0.9"
                className="pointer-events-none"
              >
                {step.count.toLocaleString()} ({step.percentage}%)
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// Training Request Card Component
const TrainingRequestCard: React.FC<{
  request: TrainingRequest;
  onReschedule: (request: TrainingRequest) => void;
  onConfirm: (request: TrainingRequest) => void;
  onMarkComplete: (request: TrainingRequest) => void;
  isLoading: boolean;
}> = ({ request, onReschedule, onConfirm, onMarkComplete, isLoading }) => {
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const getStatusBadge = (status: string) => {
    const statusStyles: Record<string, string> = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      CONFIRMED: 'bg-blue-100 text-blue-800',
      COMPLETED: 'bg-green-100 text-green-800',
      CANCELLED: 'bg-red-100 text-red-800',
    };
    return statusStyles[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium">
            {request.userName.split(' ').map(n => n[0]).join('').slice(0, 2)}
          </div>
          <div>
            <h4 className="font-semibold text-gray-900">{request.userName}</h4>
            <p className="text-sm text-gray-500">{request.userEmail}</p>
          </div>
        </div>
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(request.status)}`}>
          {request.status}
        </span>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="w-4 h-4" />
          <span>Preferred: {formatDate(request.preferredSlot1)}</span>
        </div>
        {request.preferredSlot2 && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Calendar className="w-4 h-4" />
            <span>Alt 1: {formatDate(request.preferredSlot2)}</span>
          </div>
        )}
        {request.preferredSlot3 && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Calendar className="w-4 h-4" />
            <span>Alt 2: {formatDate(request.preferredSlot3)}</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-sm text-gray-600">
          {request.meetingType === 'zoom' ? (
            <Video className="w-4 h-4" />
          ) : (
            <Phone className="w-4 h-4" />
          )}
          <span>{request.meetingType === 'zoom' ? 'Zoom Meeting' : `Phone: ${request.phoneNumber || 'TBD'}`}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Clock className="w-4 h-4" />
          <span>Timezone: {request.timezone}</span>
        </div>
        {request.confirmedSlot && (
          <div className="flex items-center gap-2 text-sm text-green-600 font-medium">
            <Check className="w-4 h-4" />
            <span>Confirmed: {formatDate(request.confirmedSlot)}</span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 pt-3 border-t border-gray-100">
        {request.status === 'PENDING' && (
          <>
            <button
              onClick={() => onReschedule(request)}
              disabled={isLoading}
              className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium flex items-center justify-center gap-1 disabled:opacity-50"
            >
              <Calendar className="w-4 h-4" />
              Reschedule
            </button>
            <button
              onClick={() => onConfirm(request)}
              disabled={isLoading}
              className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center justify-center gap-1 disabled:opacity-50"
            >
              <Check className="w-4 h-4" />
              Confirm
            </button>
          </>
        )}
        {request.status === 'CONFIRMED' && (
          <button
            onClick={() => onMarkComplete(request)}
            disabled={isLoading}
            className="flex-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium flex items-center justify-center gap-1 disabled:opacity-50"
          >
            <Check className="w-4 h-4" />
            Mark Complete
          </button>
        )}
        {request.status === 'COMPLETED' && (
          <div className="flex-1 text-center text-green-600 text-sm font-medium py-2">
            Training Completed
          </div>
        )}
      </div>
    </div>
  );
};

// Main Recruit Tracking Section
export const RecruitTrackingSection: React.FC = () => {
  const [recruitData, setRecruitData] = useState<TeamRecruitData | null>(null);
  const [trainingRequests, setTrainingRequests] = useState<TrainingRequest[]>([]);
  const [selectedStep, setSelectedStep] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [isLoadingRecruits, setIsLoadingRecruits] = useState(true);
  const [isLoadingTraining, setIsLoadingTraining] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Modal states
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<TrainingRequest | null>(null);
  const [newScheduleDate, setNewScheduleDate] = useState('');
  const [confirmSlot, setConfirmSlot] = useState('');
  const [meetingType, setMeetingType] = useState<'phone' | 'zoom'>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');

  // Fetch recruits data
  const fetchRecruits = useCallback(async () => {
    try {
      setIsLoadingRecruits(true);
      setError('');

      // Fetch real data from API
      const response = await salesManagerService.getTeamRecruits({
        step: selectedStep || undefined,
        page: currentPage,
        limit: 10,
      });

      setRecruitData(response);
      setTotalPages(Math.ceil(response.totalRecruits / 10));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load recruit data');
    } finally {
      setIsLoadingRecruits(false);
    }
  }, []);

  // Fetch training requests
  const fetchTrainingRequests = useCallback(async () => {
    try {
      setIsLoadingTraining(true);

      const response = await salesManagerService.getTrainingRequests({
        status: statusFilter || undefined,
        page: currentPage,
        limit: 10,
      });

      setTrainingRequests(response.requests);
      setTotalPages(response.pagination.totalPages);
    } catch (err: any) {
      console.error('Failed to load training requests:', err);
    } finally {
      setIsLoadingTraining(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchRecruits();
    fetchTrainingRequests();
  }, [fetchRecruits, fetchTrainingRequests]);

  useEffect(() => {
    fetchTrainingRequests();
  }, [fetchTrainingRequests, currentPage]);

  const handleStepClick = (step: number) => {
    setSelectedStep(prev => prev === step ? null : step);
  };

  const handleReschedule = (request: TrainingRequest) => {
    setSelectedRequest(request);
    setNewScheduleDate('');
    setShowRescheduleModal(true);
  };

  const handleConfirm = (request: TrainingRequest) => {
    setSelectedRequest(request);
    setConfirmSlot(request.preferredSlot1);
    setMeetingType(request.meetingType);
    setPhoneNumber(request.phoneNumber || '');
    setShowConfirmModal(true);
  };

  const handleMarkComplete = async (request: TrainingRequest) => {
    try {
      setActionLoading(true);
      await salesManagerService.markTrainingComplete({ trainingScheduleId: request.id });

      setSuccessMessage(`Training marked complete for ${request.userName}`);
      fetchTrainingRequests();
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to mark training complete');
    } finally {
      setActionLoading(false);
    }
  };

  const submitReschedule = async () => {
    if (!selectedRequest || !newScheduleDate) return;

    try {
      setActionLoading(true);
      await salesManagerService.rescheduleTraining({
        trainingScheduleId: selectedRequest.id,
        newSlot: newScheduleDate,
      });

      setSuccessMessage(`Training rescheduled for ${selectedRequest.userName}`);
      setShowRescheduleModal(false);
      fetchTrainingRequests();
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reschedule training');
    } finally {
      setActionLoading(false);
    }
  };

  const submitConfirm = async () => {
    if (!selectedRequest || !confirmSlot) return;

    try {
      setActionLoading(true);
      await salesManagerService.confirmTraining({
        trainingScheduleId: selectedRequest.id,
        confirmedSlot: confirmSlot,
        meetingType: meetingType,
        phoneNumber: meetingType === 'phone' ? phoneNumber : undefined,
      });

      setSuccessMessage(`Training confirmed for ${selectedRequest.userName}`);
      setShowConfirmModal(false);
      fetchTrainingRequests();
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to confirm training');
    } finally {
      setActionLoading(false);
    }
  };

  if (isLoadingRecruits && !recruitData) {
    return (
      <div className="bg-white rounded-lg shadow p-8">
        <div className="flex items-center justify-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">Loading recruit tracking...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Recruit Tracking</h2>
        <p className="text-gray-600">Track all recruits under your team through the 9-step onboarding funnel</p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2">
          <Check className="w-5 h-5 text-green-600" />
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      {/* Stats Cards */}
      {recruitData && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Recruits</p>
                <p className="text-2xl font-bold text-gray-900">{recruitData.totalRecruits}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Active Recruits</p>
                <p className="text-2xl font-bold text-gray-900">{recruitData.activeRecruits}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gray-100 rounded-lg">
                <X className="w-6 h-6 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Inactive (30-60 days)</p>
                <p className="text-2xl font-bold text-gray-900">{recruitData.inactiveRecruits}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Funnel Visualization */}
      {recruitData && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900">9-Step Onboarding Funnel</h3>
              <p className="text-sm text-gray-600">Click on a step to filter recruits</p>
            </div>
            <button
              onClick={fetchRecruits}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
          <FunnelVisualization
            data={recruitData.funnelStats}
            selectedStep={selectedStep}
            onStepClick={handleStepClick}
          />
        </div>
      )}

      {/* Training Request Management */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Training Requests</h3>
              <p className="text-sm text-gray-600">Manage training time requests from your recruits</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="PENDING">Pending</option>
                  <option value="CONFIRMED">Confirmed</option>
                  <option value="COMPLETED">Completed</option>
                </select>
              </div>
              <button
                onClick={fetchTrainingRequests}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          {isLoadingTraining ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading training requests...</span>
            </div>
          ) : trainingRequests.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">No training requests found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {trainingRequests.map((request) => (
                <TrainingRequestCard
                  key={request.id}
                  request={request}
                  onReschedule={handleReschedule}
                  onConfirm={handleConfirm}
                  onMarkComplete={handleMarkComplete}
                  isLoading={actionLoading}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 border border-gray-300 rounded-md disabled:opacity-50 hover:bg-gray-100"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 border border-gray-300 rounded-md disabled:opacity-50 hover:bg-gray-100"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Reschedule Modal */}
      {showRescheduleModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Reschedule Training</h3>
            <p className="text-sm text-gray-600 mb-4">
              Reschedule training for {selectedRequest.userName}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">New Date & Time</label>
              <input
                type="datetime-local"
                value={newScheduleDate}
                onChange={(e) => setNewScheduleDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowRescheduleModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={submitReschedule}
                disabled={!newScheduleDate || actionLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {actionLoading ? 'Saving...' : 'Reschedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirmModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Training</h3>
            <p className="text-sm text-gray-600 mb-4">
              Confirm training for {selectedRequest.userName}
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirmed Date & Time</label>
                <select
                  value={confirmSlot}
                  onChange={(e) => setConfirmSlot(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value={selectedRequest.preferredSlot1}>
                    {new Date(selectedRequest.preferredSlot1).toLocaleString()}
                  </option>
                  {selectedRequest.preferredSlot2 && (
                    <option value={selectedRequest.preferredSlot2}>
                      {new Date(selectedRequest.preferredSlot2).toLocaleString()}
                    </option>
                  )}
                  {selectedRequest.preferredSlot3 && (
                    <option value={selectedRequest.preferredSlot3}>
                      {new Date(selectedRequest.preferredSlot3).toLocaleString()}
                    </option>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Meeting Type</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      value="phone"
                      checked={meetingType === 'phone'}
                      onChange={() => setMeetingType('phone')}
                      className="text-blue-600"
                    />
                    <Phone className="w-4 h-4" />
                    Phone
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      value="zoom"
                      checked={meetingType === 'zoom'}
                      onChange={() => setMeetingType('zoom')}
                      className="text-blue-600"
                    />
                    <Video className="w-4 h-4" />
                    Zoom
                  </label>
                </div>
              </div>
              {meetingType === 'phone' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+1 555-123-4567"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={submitConfirm}
                disabled={!confirmSlot || actionLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {actionLoading ? 'Confirming...' : 'Confirm Training'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecruitTrackingSection;
